// Sopwith 3D - keyboard input.
//
// Turns held keys into the per-tick command word the simulation consumes.
// Flip, sound, missile and starburst are edge-triggered (one action per key
// press) exactly as in the original; everything else repeats while held.

import {
  K_ACCEL,
  K_BOMB,
  K_DEACC,
  K_FLAPD,
  K_FLAPU,
  K_FLIP,
  K_HOME,
  K_MISSILE,
  K_SHOT,
  K_SOUND,
  K_STARBURST,
} from '../sim/constants';

export const DEFAULT_KEYMAP: Record<string, number> = {
  Comma: K_FLAPU,
  ArrowUp: K_FLAPU,
  ArrowLeft: K_FLAPU, // the original's elevator keys
  Slash: K_FLAPD,
  ArrowDown: K_FLAPD,
  ArrowRight: K_FLAPD,
  Period: K_FLIP,
  KeyX: K_ACCEL,
  KeyZ: K_DEACC,
  Space: K_SHOT,
  KeyB: K_BOMB,
  KeyH: K_HOME,
  KeyS: K_SOUND,
  KeyV: K_MISSILE,
  KeyC: K_STARBURST,
};

const EDGE_KEYS = K_FLIP | K_SOUND | K_MISSILE | K_STARBURST;

export class Keyboard {
  private held = 0;
  private pressed = 0;

  constructor(
    target: Window | HTMLElement = window,
    private readonly keymap: Record<string, number> = DEFAULT_KEYMAP,
  ) {
    target.addEventListener('keydown', this.onDown as EventListener);
    target.addEventListener('keyup', this.onUp as EventListener);
    window.addEventListener('blur', () => {
      this.held = 0;
    });
  }

  private onDown = (e: KeyboardEvent): void => {
    const bit = this.keymap[e.code];
    if (bit === undefined) return;
    e.preventDefault();
    if (e.repeat) return;
    this.held |= bit;
    this.pressed |= bit & EDGE_KEYS;
  };

  private onUp = (e: KeyboardEvent): void => {
    const bit = this.keymap[e.code];
    if (bit === undefined) return;
    this.held &= ~bit;
  };

  /** The command word for the next tick; consumes edge-triggered presses. */
  poll(): number {
    const keys = (this.held & ~EDGE_KEYS) | this.pressed;
    this.pressed = 0;
    return keys;
  }
}
